import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  Loader2,
  ArrowLeft,
  ArrowRight,
  KeyRound,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { track, EVENTS } from "@/lib/analytics";

/**
 * OwnerLogin — email -> OTP login flow for restaurant owners.
 *
 * Flow:
 *   1. User enters their business email -> POST /api/owner/login.
 *      Response includes a session_id (always) and a dev_code (only when
 *      Resend is not configured / the recipient is unverified — used to
 *      keep local dev working without network egress).
 *   2. User enters the 6-digit code -> POST /api/owner/verify.
 *      Success returns an `owner_token` which the parent persists via
 *      `onAuthed`.
 */
export default function OwnerLogin({ onAuthed }) {
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/owner/login", { email });
      setSessionId(res.data.session_id);
      setDevCode(res.data.dev_code || "");
      setStep("code");
    } catch (err) {
      toast.error("Couldn't start login", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/owner/verify", {
        session_id: sessionId,
        code,
      });
      toast.success("Welcome back");
      track(EVENTS.OWNER_LOGIN, { email });
      onAuthed(res.data.owner_token, res.data.email);
    } catch (err) {
      toast.error("Verification failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="owner-login"
      className="min-h-screen w-full flex items-center justify-center bg-[#FDFBF7] p-6"
    >
      <div className="w-full max-w-sm bg-white rounded-3xl border border-[#E5E1D8] shadow-lg p-7">
        <Link
          to="/"
          className="text-xs text-[#647466] hover:text-[#D95D39] inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back to map
        </Link>
        <div className="mx-auto w-12 h-12 rounded-full bg-[#D95D39] flex items-center justify-center mt-3">
          <Crown className="w-6 h-6 text-white" />
        </div>
        <h1 className="mt-3 font-display text-xl font-medium text-center text-[#2A3B2C]">
          Owner dashboard
        </h1>
        <p className="text-sm text-[#647466] text-center mt-1">
          {step === "email"
            ? "Sign in with the email used to claim your restaurant."
            : `We sent a 6-digit code to ${email}`}
        </p>

        <AnimatePresence mode="wait">
          {step === "email" ? (
            <motion.form
              key="email"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              onSubmit={sendCode}
              className="mt-5 space-y-3"
            >
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#647466]">
                Business email
              </Label>
              <Input
                data-testid="owner-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@alkoncierge.com"
                className="border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl h-11"
              />
              <button
                type="submit"
                disabled={loading || !email}
                data-testid="owner-send-code"
                className="w-full h-11 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Send code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </motion.form>
          ) : (
            <motion.form
              key="code"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              onSubmit={verifyCode}
              className="mt-5 space-y-3"
            >
              {devCode ? (
                <div className="rounded-xl border border-dashed border-[#E1A624] bg-[#FFFBF0] px-3 py-2 flex items-center justify-between gap-3 text-xs">
                  <span>
                    <span className="font-bold text-[#E1A624]">DEMO</span>{" "}
                    code: <code className="font-bold">{devCode}</code>
                  </span>
                  <button
                    type="button"
                    onClick={() => setCode(devCode)}
                    className="inline-flex items-center gap-1 text-[#D95D39]"
                  >
                    <Copy className="w-3 h-3" />
                    Use
                  </button>
                </div>
              ) : (
                <div
                  data-testid="owner-code-sent-banner"
                  className="rounded-xl border border-[#2E7D5B]/40 bg-[#F0F8F4] px-3 py-2 text-xs text-[#1F5C42] flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D5B]" />
                  We just emailed your 6-digit code. Check your inbox.
                </div>
              )}
              <Input
                data-testid="owner-code-input"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                className="text-center text-2xl tracking-[0.5em] font-display h-14 border-[#E5E1D8] focus-visible:ring-[#D95D39] rounded-xl"
              />
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="owner-verify-submit"
                className="w-full h-11 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Verify
                  </>
                )}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
